import {
  SECURITY_ANSWERS_REQUIRED,
  SECURITY_ANSWER_MIN,
  SECURITY_QUESTIONS,
  normalizeAnswer,
} from '@ehr/shared';

/**
 * ehr#311 - "Set new answers" in the Demographics Security Questions section (Jay 10-12: staff set
 * new ones for a client who forgot, and never see them; Chase's CLIENT_SECURITY_QUESTIONS_10-08-V1):
 * pick 2 of the questions the practice offers, type each answer twice. The API re-checks the same
 * rules (securityAnswersSchema) and keeps only a hash.
 */

export interface Pick {
  id: string;
  answer: string;
  confirm: string;
}

export const PICK = SECURITY_ANSWERS_REQUIRED;

/** The practice's offered questions in catalog order; every question if fewer than 2 are offered. */
export function offeredQuestions(offered: readonly string[] | undefined) {
  const list = offered ? SECURITY_QUESTIONS.filter((q) => offered.includes(q.id)) : [];
  return list.length >= PICK ? list : [...SECURITY_QUESTIONS];
}

const order = (id: string) => SECURITY_QUESTIONS.findIndex((q) => q.id === id);

/** Pick or un-pick a question, keeping at most 2 and the catalog order. */
export function togglePick(picks: Pick[], id: string): Pick[] {
  if (picks.some((p) => p.id === id)) return picks.filter((p) => p.id !== id);
  if (picks.length >= PICK) return picks;
  return [...picks, { id, answer: '', confirm: '' }].sort((a, b) => order(a.id) - order(b.id));
}

/** { questionId: message } per problem, `_` for the whole form; empty = ready to save. */
export function validatePicks(picks: Pick[]): Record<string, string> {
  const errs: Record<string, string> = {};
  if (picks.length !== PICK) errs._ = `Choose ${PICK} questions.`;
  for (const p of picks) {
    if (normalizeAnswer(p.answer).length < SECURITY_ANSWER_MIN)
      errs[p.id] = `Use at least ${SECURITY_ANSWER_MIN} characters.`;
    else if (normalizeAnswer(p.answer) !== normalizeAnswer(p.confirm))
      errs[p.id] = 'Answers do not match.';
  }
  const [a, b] = picks;
  if (
    picks.length === PICK &&
    a &&
    b &&
    !errs[a.id] &&
    !errs[b.id] &&
    normalizeAnswer(a.answer) === normalizeAnswer(b.answer)
  )
    errs[b.id] = 'Use a different answer for each question.';
  return errs;
}
